<template>
    <div class="sidebar-section">
        <h6 class="fw-bold mb-3">Impostazioni template</h6>

        <div class="mb-2">
            <label class="form-label small">Nome template</label>
            <input class="form-control form-control-sm" type="text" v-model="model.name" @input="checkName" />
            <small class="text-danger" v-if="!model.name.trim()">Campo obbligatorio!</small>
            <small class="text-danger" v-if="nameError">{{nameError}}</small>
        </div>

        <div class="mb-2">
            <label class="form-label small">Formato predefinito</label>
            <select class="form-select form-select-sm" :value="selectedFormatName" @change="applyFormat(($event.target as any).value)">
                <option value="">— Personalizzato —</option>
                <optgroup v-for="(formats, group) in formatGroups" :key="group" :label="group">
                    <option v-for="f in formats" :key="f.name" :value="f.name">{{ f.name }}</option>
                </optgroup>
            </select>
        </div>

        <div class="row mb-2">
            <div class="col-6">
                <label class="form-label small">Larghezza (cm)</label>
                <input class="form-control form-control-sm" type="number" step="0.01" min="0" v-model.number="tempWidth" />
            </div>
            <div class="col-6">
                <label class="form-label small">Altezza (cm)</label>
                <input class="form-control form-control-sm" type="number" step="0.01" min="0" v-model.number="tempHeight" />
            </div>
        </div>

        <div class="mb-2">
            <label class="form-label small">Border Radius</label>
            <input class="form-control form-control-sm" type="text" v-model="model.borderRadius" placeholder="10px" />
        </div>

        <div class="mb-2">
            <label class="form-label small">Colore di sfondo</label>
            <input class="form-control form-control-sm" type="color" v-model="model.backgroundColor" />
        </div>

        <hr/>
        <div class="mb-2">
            <label class="form-label small d-flex align-items-center">
                <toggler class="me-1" v-model="model.isActive"></toggler>
                Template attivo
            </label>
        </div>

    </div>
</template>

<script lang="ts">
import * as OM from '@/model';
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { pxToCm, cmToPx } from '@/utils/units';
import { PrintTemplateClient } from '@/services/Services';
import store from '@/store';

interface PresetFormat {
    group: string;
    name: string;
    width: number;  // cm
    height: number; // cm
}

const PRESET_FORMATS: PresetFormat[] = [
    // Etichette
    { group: 'Etichette',    name: 'Rettangolare (10×5 cm)',             width: 10,    height: 5 },
    { group: 'Etichette',    name: 'Rettangolare (15×10 cm)',            width: 15,    height: 10 },
    { group: 'Etichette',    name: 'Rettangolare (7.5×13 cm)',            width: 7.5,    height: 13 },
    { group: 'Etichette',    name: 'Rettangolare (7.5×10 cm)',            width: 7.5,    height: 10 },
    { group: 'Etichette',    name: 'Rettangolare (5×7.5 cm)',            width: 5,    height: 7.5 },
//     // ISO
//     { group: 'Formato ISO',  name: 'A6 — 10.5×14.8 cm',                 width: 10.5,  height: 14.8 },
//     { group: 'Formato ISO',  name: 'A5 — 14.8×21 cm',                   width: 14.8,  height: 21 },
//     { group: 'Formato ISO',  name: 'A4 — 21×29.7 cm',                   width: 21,    height: 29.7 },
//     { group: 'Formato ISO',  name: 'A4 orizzontale — 29.7×21 cm',       width: 29.7,  height: 21 },
//     { group: 'Formato ISO',  name: 'A3 — 29.7×42 cm',                   width: 29.7,  height: 42 },
//     { group: 'Formato ISO',  name: 'A3 orizzontale — 42×29.7 cm',       width: 42,    height: 29.7 },
//     // USA
//     { group: 'Formato USA',  name: 'Letter — 21.6×27.9 cm',             width: 21.59, height: 27.94 },
//     { group: 'Formato USA',  name: 'Legal — 21.6×35.6 cm',              width: 21.59, height: 35.56 },
//     { group: 'Formato USA',  name: 'Tabloid — 27.9×43.2 cm',            width: 27.94, height: 43.18 },
];

const MATCH_TOLERANCE = 0.05; // cm

@Options({})
export default class TemplateSettings extends Vue {

    @Prop() model: OM.PrintTemplate;

    nameError: string = "";

    localTempWidth: number = null;
    localTempHeight: number = null;

    get tempWidth(): number {
        return this.localTempWidth ?? pxToCm(this.model.width);
    }
    set tempWidth(value: number) {
        this.localTempWidth = value;
        this.model.width = cmToPx(value);
    }

    get tempHeight(): number {
        return this.localTempHeight ?? pxToCm(this.model.height);
    }
    set tempHeight(value: number) {
        this.localTempHeight = value;
        this.model.height = cmToPx(value);
    }

    get formatGroups(): Record<string, PresetFormat[]> {
        return PRESET_FORMATS.reduce((acc, f) => {
            if (!acc[f.group]) acc[f.group] = [];
            acc[f.group].push(f);
            return acc;
        }, {} as Record<string, PresetFormat[]>);
    }

    get selectedFormatName(): string {
        const w = this.tempWidth;
        const h = this.tempHeight;
        const match = PRESET_FORMATS.find(
            f => Math.abs(f.width - w) < MATCH_TOLERANCE && Math.abs(f.height - h) < MATCH_TOLERANCE
        );
        return match?.name ?? '';
    }

    applyFormat(name: string) {
        const format = PRESET_FORMATS.find(f => f.name === name);
        if (!format) return;
        this.tempWidth = format.width;
        this.tempHeight = format.height;
    }
    
    debouncer = null;
    checkName(){
        clearTimeout(this.debouncer);
        this.nameError = "";
        this.debouncer = setTimeout(() => {
            PrintTemplateClient.checkNameExisting(
                this.model.name,
                this.model.identifier,
                false
            )
            .then(x => {
                if(x){
                    this.nameError = "Nome template già in uso";
                }
            });
        }, 600);
    }

}
</script>
